import React, { useEffect } from 'react';
import { Drawer, IconButton, Box } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useSettingsStore } from '@core/store';

interface EditorSidebarProps {
    open: boolean;
    onClose: () => void;
    stacked?: boolean;
    children: React.ReactNode;
}

const EditorSidebar: React.FC<EditorSidebarProps> = ({ open, onClose, stacked = false, children }) => {
    const { darkMode } = useSettingsStore();

    // Handle ESC key
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && open) {
                onClose();
            }
        };

        window.addEventListener('keyup', handleEscKey);
        return () => window.removeEventListener('keyup', handleEscKey);
    }, [open, onClose]);

    // Calculate width based on breakpoint
    const getWidth = () => {
        if (typeof window === 'undefined') return '50%';

        const width = window.innerWidth;
        if (width < 600) return '100%'; // xs
        if (width < 960) return '75%'; // sm
        if (width < 1280) return '75%'; // md
        if (width < 1920) return '50%'; // lg
        return '50%'; // xl
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            variant="temporary"
            hideBackdrop={false}
            ModalProps={{
                keepMounted: false
            }}
            PaperProps={{
                className: `editor-sidebar ${stacked ? 'editor-sidebar-stacked' : 'editor-sidebar-standard'}`,
                sx: {
                    width: getWidth(),
                    bgcolor: darkMode ? 'var(--dark-bg-lighter)' : 'white',
                    color: darkMode ? 'white' : 'var(--gray-dark)',
                    zIndex: 1300,
                    boxShadow: '-4px 0px 12px rgba(0, 0, 0, 0.3)',
                    overflow: 'hidden'
                }
            }}
            sx={{
                zIndex: 1300,
                '& .MuiDrawer-paper': {
                    borderLeft: 'none'
                }
            }}
        >
            {/* Close Button */}
            <Box className="close-button">
                <IconButton
                    onClick={onClose}
                    sx={{
                        color: darkMode ? 'white' : 'var(--gray-dark)',
                        backgroundColor: 'transparent',
                        borderRadius: 0,
                        padding: '5px',
                        '&:hover': {
                            backgroundColor: darkMode ? 'var(--gray-lite)' : 'var(--gray-lighter)'
                        }
                    }}
                >
                    <CloseIcon sx={{ fontSize: 24 }} />
                </IconButton>
            </Box>

            {/* Content */}
            <Box
                sx={{
                    height: '100%',
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {children}
            </Box>
        </Drawer>
    );
};

export default EditorSidebar;
